import { SESSION_STORAGE_APP_STATE_KEY, SESSION_STORAGE_MOCKUP_STATE_KEY } from 'config/consts';
import { getEntryModeFromWindow, isExistingSiteEntryMode } from 'config/entry-mode';
import { windowUtils } from './window';
import { AppState } from 'stores/appStore';
import { MockupState } from 'stores/mockupStore';
import { CategoryKey } from 'config/categories';
import { productParamsById, productsByCategory } from 'config/products';
import { normalizeSelectedProducts } from 'utils/product-selection';

const setAppState = (state: AppState) => {
  windowUtils.getSafeWindow()?.sessionStorage.setItem(SESSION_STORAGE_APP_STATE_KEY, JSON.stringify(state));
};

const getInitialAppState = (): AppState => {
  const window = windowUtils.getSafeWindow();
  const appStateString = window?.sessionStorage.getItem(SESSION_STORAGE_APP_STATE_KEY);
  const entryMode = getEntryModeFromWindow();
  const selectedCategory = CategoryKey.BestSellers;
  const [firstProductId] = productsByCategory[selectedCategory];
  const defaultFirstProduct = { id: firstProductId, ...productParamsById[firstProductId] };

  const defaultState: Omit<AppState, 'metaSiteId' | 'entryMode'> = {
    selectedProducts: isExistingSiteEntryMode(entryMode) ? [] : [defaultFirstProduct],
    currentStep: undefined,
    localFile: undefined,
    storeName: undefined,
    mediaUrl: undefined,
    siteUrl: undefined,
    selectedCategory,
    wasStoresInstalledAtEntry: undefined,
  };

  const storedState: Partial<AppState> = appStateString ? JSON.parse(appStateString) : {};
  const metaSiteId = window?.__META_SITE_ID__;
  const canRestoreSession = storedState.metaSiteId === metaSiteId && storedState.entryMode === entryMode;

  const restoredState = canRestoreSession ? storedState : {};

  return {
    ...defaultState,
    ...restoredState,
    selectedProducts: canRestoreSession
      ? normalizeSelectedProducts(restoredState.selectedProducts)
      : defaultState.selectedProducts,
    entryMode,
    metaSiteId,
  };
};

// Mockups live in their own (non-persisted) store, but the Printful-connection
// redirect reloads the page, wiping in-memory state. Persist the generated
// mockups per metaSite so the store-ready page can show the real items.
const setMockups = (mockups: MockupState['mockups']) => {
  const window = windowUtils.getSafeWindow();
  const metaSiteId = window?.__META_SITE_ID__;
  window?.sessionStorage.setItem(SESSION_STORAGE_MOCKUP_STATE_KEY, JSON.stringify({ metaSiteId, mockups }));
};

const getInitialMockups = (): MockupState['mockups'] => {
  const window = windowUtils.getSafeWindow();
  const stored = window?.sessionStorage.getItem(SESSION_STORAGE_MOCKUP_STATE_KEY);

  if (!stored) {
    return {};
  }

  const { metaSiteId, mockups }: { metaSiteId?: string; mockups?: MockupState['mockups'] } = JSON.parse(stored);

  return metaSiteId === window?.__META_SITE_ID__ ? mockups ?? {} : {};
};

export const sessionStorageUtils = {
  setAppState,
  getInitialAppState,
  setMockups,
  getInitialMockups,
};
